<script lang="ts" setup>
import { useResponsive } from "@composables"
import {
    DEBUT_INCONNU,
    GAZ_ANNONCE,
    GAZ_BOUTON,
    GAZ_MESSAGE_MANQUANT,
    GAZ_TOGGLE_FERMER,
    GAZ_TOGGLE_OUVRIR,
    TRACKING_EVENTS,
    RECU_BOUTON,
    consentDateFin,
    gazDetail,
    gazPhrase,
    recuTexte,
    toFrDay,
} from "@constant"
import { isPceConsentUnchanged } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import {
    computed,
    inject,
    nextTick,
    onActivated,
    onDeactivated,
    ref,
} from "vue"
import { date, object, string } from "yup"

const store = useFormStore()
const { snapshot, send } = store

const { isSmallViewport } = useResponsive()

const { $track } = inject<TrackingPlugin>("tracking")!

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            pce_idsource: string()
                .matches(/^\d{14}$/, {
                    message: "Le numéro de compteur doit contenir 14 chiffres",
                    excludeEmptyString: true,
                })
                .required("Indiquez le numéro de compteur")
                .default(snapshot.value.context.pce_idsource),
            date_from_data: date()
                .required("Sélectionnez une date de début de contrat")
                .default(snapshot.value.context.date_from_data),
        })
    ),
})

const { value: pce_idsource } = useField<string>("pce_idsource")
const { value: date_from_data } = useField<Date | string>("date_from_data")

// =============================================================
// Autorisation (DEV-2274, spécification 2.1 et 2.3)
// =============================================================

/** Identifiant de la case, cible du focus et du message. */
const CONSENT_INPUT_ID = "consent_pce"
const CONSENT_MESSAGE_ID = "consent_pce_missing"
const CONSENT_DETAIL_ID = "consent_pce_detail"

/** La case vit dans le contexte de la machine (`UPDATE_FIELD`). */
const consentPce = computed<boolean>({
    get: () => snapshot.value.context.consent_pce === true,
    set: (value) =>
        send({ type: "UPDATE_FIELD", field: "consent_pce", value: !!value }),
})

const detailOpen = ref(false)

const dateFin = computed(() => consentDateFin(store.cstbRecord))

/**
 * Reçu (décision 7) : consentement actif, compteur et date inchangés. Calculé
 * sur les champs SAISIS, avec la même règle que la garde de la machine.
 */
const isReceipt = computed(() =>
    isPceConsentUnchanged(store.pce, pce_idsource.value, date_from_data.value)
)

const receiptText = computed(() =>
    recuTexte(toFrDay(store.pce?.consent_signed_at))
)

const phrase = computed(() =>
    gazPhrase({ pce: pce_idsource.value ?? "", fin: dateFin.value })
)

const detail = computed(() =>
    gazDetail({
        debut: toFrDay(date_from_data.value) ?? DEBUT_INCONNU,
        fin: dateFin.value,
        titulaire: `${snapshot.value.context.firstname} ${snapshot.value.context.lastname}`,
        cp: snapshot.value.context.address?.zipcode ?? "",
    })
)

/** Message au clic sans case : seulement après un clic, tant que la case manque. */
const showMissing = computed(
    () =>
        snapshot.value.matches({ Step2: { FormGaz: "consent_missing" } }) &&
        !consentPce.value &&
        !isReceipt.value
)

const confirmLabel = computed(() =>
    isReceipt.value ? RECU_BOUTON : GAZ_BOUTON
)

function focusConsent() {
    nextTick(() => {
        document.getElementById(CONSENT_INPUT_ID)?.focus()
    })
}

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    // Met à jour le contexte de la machine
    send({
        type: "UPDATE_FIELD",
        field: "pce_idsource",
        value: values.pce_idsource,
    })
    send({
        type: "UPDATE_FIELD",
        field: "date_from_data",
        value: values.date_from_data,
    })

    const consentMissing = !consentPce.value && !isReceipt.value

    if (consentMissing) {
        // Événement existant, motif ajouté (pas d'événement nouveau).
        $track(TRACKING_EVENTS.FORM_INAVLID, {
            formId: snapshot.value.context.formId,
            step: snapshot.value.context.step + 1,
            motif: "consent_missing",
        })
    }

    // La machine décide : sans case, repli vers `consent_missing`, sans
    // aucun appel de signature.
    send({
        type: "SUBMIT",
    })

    if (consentMissing) focusConsent()
}

const onSubmit = handleSubmit(onSuccess, onError)

/** Erreur de signature, affichée (elle restait invisible). */
const signErrorMessage = computed(() =>
    snapshot.value.context.error_sign_pce
        ? "Une erreur est survenue lors du processus d'autorisation de vos données. Veuillez réessayer plus tard."
        : null
)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})

const visible = ref(false)

const visibleConsent = ref(false)
</script>

<template>
    <FormCard
        class="form-gaz"
        title="L'essentiel"
        sub-title="Gaz : Compteur"
        :confirm-label="confirmLabel"
        :horizontal-btn="isSmallViewport"
        @submit="
            () => {
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-gaz',
                    buttonName: confirmLabel,
                })
                onSubmit()
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-gaz',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({
                Step2: { FormGaz: 'updateField' },
            }) ||
            snapshot.matches({
                Step2: { FormGaz: 'sign' },
            })
        "
    >
        <!-- Phrase d'aide -->
        <div class="form-gaz__sentence">
            <img
                src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1745852810/icon_smile_g3q7sb.png"
                alt="Smiley icon"
                loading="lazy"
                class="form-gaz__smile-icon"
            />
            <p class="md:text-center font-medium md:font-semibold">
                Vous avez mentionné que vous utilisiez/utilisez du gaz, donc
                nous avons besoin d’identifier votre compteur.
            </p>
        </div>
        <!-- Formulaire -->
        <div class="form-gaz__form">
            <!-- Numéro du compteur -->
            <div class="form-gaz__pce-input-wrapper">
                <BaseInputWrapper
                    label="Numéro de compteur (PCE)"
                    input-id="pce_idsource"
                    :error="errors.pce_idsource"
                >
                    <BaseInputText
                        id="pce_idsource"
                        v-model="pce_idsource"
                        :invalid="!!errors.pce_idsource"
                        placeholder="14 chiffres"
                    />
                </BaseInputWrapper>
                <div class="form-gaz__pce-button">
                    <img
                        src="https://res.cloudinary.com/dcoutwkb1/image/upload/w_120,q_auto,f_auto/v1746454894/4ee22d4cc24d61ab00885c63006a113b89a54e11_zk5gqb.png"
                        alt="Icône d'information"
                        loading="lazy"
                    />
                    <BaseButton
                        text
                        label="Où trouver mon numéro de compteur ?"
                        @click="visible = !visible"
                    ></BaseButton>
                </div>
                <DialogPce v-model:visible="visible"></DialogPce>
            </div>
            <!-- Date de début de contrat* -->
            <BaseInputWrapper
                label="Date de début de contrat*"
                input-id="date_from_data"
                hint="* Chez votre fournisseur actuel"
                :error="errors.date_from_data"
            >
                <BaseDatePicker
                    id="date_from_data"
                    placeholder="--/--/--"
                    v-model="date_from_data"
                    date-format="dd/mm/yy"
                    :invalid="!!errors.date_from_data"
                    :max-date="new Date()"
                />
            </BaseInputWrapper>
        </div>

        <!-- Autorisation (DEV-2274), au-dessus des boutons -->
        <div class="form-gaz__consent">
            <!-- Reçu : autorisation déjà donnée, compteur et date inchangés -->
            <p v-if="isReceipt" class="form-gaz__receipt">
                {{ receiptText }}
            </p>

            <div v-else class="form-gaz__consent-block">
                <div class="form-gaz__consent-head">
                    <BaseCheckbox
                        v-model="consentPce"
                        binary
                        :input-id="CONSENT_INPUT_ID"
                        :invalid="showMissing"
                        :pt="{
                            input: {
                                'aria-describedby': showMissing
                                    ? CONSENT_MESSAGE_ID
                                    : undefined,
                            },
                        }"
                    />
                    <label :for="CONSENT_INPUT_ID">
                        <ConsentSegments :segments="phrase" />
                    </label>
                </div>

                <BaseButton
                    variant="link"
                    size="small"
                    class="form-gaz__consent-toggle"
                    :label="detailOpen ? GAZ_TOGGLE_FERMER : GAZ_TOGGLE_OUVRIR"
                    :aria-expanded="detailOpen"
                    :aria-controls="CONSENT_DETAIL_ID"
                    @click="detailOpen = !detailOpen"
                />

                <div
                    v-show="detailOpen"
                    :id="CONSENT_DETAIL_ID"
                    class="form-gaz__consent-detail"
                >
                    <p><ConsentSegments :segments="detail.lead" /></p>
                    <ul>
                        <li v-for="item in detail.items" :key="item">
                            {{ item }}
                        </li>
                    </ul>
                    <p><ConsentSegments :segments="detail.after ?? []" /></p>
                </div>
            </div>

            <BaseButton
                variant="link"
                size="small"
                class="form-gaz__more-info"
                label="En savoir plus sur mes données"
                @click="visibleConsent = !visibleConsent"
            />

            <!-- Annonce du courriel de GRDF, hors du bloc replié -->
            <div v-if="!isReceipt" class="form-gaz__announce">
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#092754"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                    <path d="m3 7 9 6 9-6"></path>
                </svg>
                <p><ConsentSegments :segments="GAZ_ANNONCE" /></p>
            </div>

            <p
                v-if="showMissing"
                :id="CONSENT_MESSAGE_ID"
                class="form-gaz__consent-missing"
                role="alert"
            >
                {{ GAZ_MESSAGE_MANQUANT }}
            </p>

            <BaseMessage v-if="signErrorMessage" severity="error">
                {{ signErrorMessage }}
            </BaseMessage>
        </div>
        <template #footer>
            <DialogConsent
                v-model:visible="visibleConsent"
                type="pce"
                :date-fin="dateFin"
                :date-debut="toFrDay(date_from_data)"
            />
        </template>
    </FormCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.form-gaz {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }

    .form-gaz__sentence {
        display: flex;
        padding: 0rem 1rem;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        align-self: stretch;

        p {
            color: #092754;
            font-size: 0.875rem;
        }

        @media (--small-viewport) {
            padding: 0.5rem;
            border-radius: 0.5rem;
            background: #f6f9fc;
        }
    }
    .form-gaz__smile-icon {
        width: 1.5rem;
        height: 1.5rem;
    }

    .form-gaz__form {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;

        @media (--small-viewport) {
            grid-template-columns: 1fr;
        }
    }

    .form-gaz__pce-input-wrapper {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .form-gaz__pce-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        img {
            width: 1.5rem;
            height: 1.5rem;
        }

        .p-button {
            height: fit-content;
            --p-button-padding-y: 0;
            --p-button-padding-x: 0.5rem;
            --p-button-text-primary-hover-background: transparent;
            --p-button-text-primary-active-background: transparent;
        }
    }

    /* Autorisation (DEV-2274) : maquette validée du dossier v1.1 */
    .form-gaz__consent {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        color: #051833;
        font-size: 0.875rem;
        line-height: 1.55;
    }

    .form-gaz__consent-block {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding: 1rem;
        border: 1.5px solid #092754;
        border-radius: 0.5rem;
        background: #f6f9fc;
    }

    .form-gaz__consent-head {
        display: grid;
        grid-template-columns: 1.625rem 1fr;
        gap: 0.625rem;
        align-items: start;

        label {
            cursor: pointer;
            font-weight: 400;
            color: #051833;
        }

        strong {
            font-weight: 700;
        }
    }

    .form-gaz__consent-toggle {
        align-self: flex-start;
        color: #0a5bc4;
        margin-left: 2.25rem;
        padding: 0;
        font-size: 0.8125rem;
        font-weight: 700;
        text-decoration: underline;
        @media (--small-viewport) {
            margin-left: 0;
        }
    }

    .form-gaz__consent-detail {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        margin-left: 2.25rem;
        padding-top: 0.75rem;
        border-top: 1px solid #dbe3ee;

        ul {
            list-style: disc;
            padding-left: 1.25rem;
        }

        @media (--small-viewport) {
            margin-left: 0;
        }
    }

    .form-gaz__receipt {
        font-weight: 600;
        color: #092754;
    }

    .form-gaz__announce {
        display: flex;
        gap: 0.625rem;
        align-items: flex-start;
        padding: 0.625rem 0.75rem;
        border: 1px solid #e7c46a;
        border-radius: 0.375rem;
        background: #fff8e6;
        font-size: 0.8125rem;

        svg {
            flex-shrink: 0;
            margin-top: 0.125rem;
        }
    }

    .form-gaz__consent-missing {
        color: var(--p-red-600, #b42318);
        font-size: 0.8125rem;
        font-weight: 600;
        text-align: right;

        @media (--small-viewport) {
            text-align: left;
        }
    }

    .form-gaz__more-info {
        align-self: flex-start;
        color: var(--p-primary-900);
        font-size: 0.75rem;
        font-weight: 400;
        text-decoration: underline;
        padding: 0;
    }
}
</style>
